import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, BehaviorSubject } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { AuthService } from './auth.service';
import { DataService } from './data.service';
import {
    APIResponse
    , Carrier
    , CarrierAnalysis
    , CarrierContract
    , CarrierDropReason
    , CarrierEquipment
    , CarrierIncident
    , CarrierLane
    , CarrierList
    , CarrierRepsAssigned
    , CarrierService
    , CarrierServiceMap
    , CustomerCarrierExclusionMap
    , DataCollection
    , FuelRate
    , FuelRateTier
    , GeocodeDetails
    , Offer
    , OpenLoad
    , OpportunityCostType
    , QuoteOrderLine
    , QuoteOrderLineCarrierServiceMap
    , QuoteOrderLineOpportunityCost
    , SearchCarrierCriteria
    , SearchCarrierLane
    , SearchCarrierListCriteria
    //, SearchCarrierSalesAvailableLoads
    , SearchCarrierSalesBookedLoads
    , SearchCarrierSalesUnverifiedLoads
    //, SearchOffer
    , SearchQuoteOrderLineCarrierService
    , TenderMaintenance
    , Zip3
} from '../models/models';

const httpOptions = {
    headers: new HttpHeaders({
        'Content-Type': 'application/json'
    })
};

@Injectable()
export class CSEService {
    //private _offers: Subject<Array<Offer>> = new BehaviorSubject<Array<Offer>>([]);
    //public readonly offers: Observable<Array<Offer>> = this._offers.asObservable();

    public availableLoads: OpenLoad[] = [];
    public tenderedLoads: OpenLoad[] = [];
    public acceptedLoads: OpenLoad[] = [];
    public dispatchedLoads: OpenLoad[] = [];
    public startedLoads: OpenLoad[] = [];
    public availableQuoteLoads: OpenLoad[] = [];
    public availableRFQLoads: OpenLoad[] = [];
    public availableOpenDedicatedLoads: OpenLoad[] = [];
    public unverifiedLoads: OpenLoad[] = [];
    public selectedDashboardLoads: OpenLoad[] = [];
    public bookedLoads: OpenLoad[] = [];
    public csrLoads = {
        'csr-available': this.availableLoads,
        'csr-quote': this.availableQuoteLoads,
        'csr-rfq': this.availableRFQLoads,
        'csr-open-dedicated': this.availableOpenDedicatedLoads,
        'csr-booked': this.bookedLoads,
        'csr-tendered': this.tenderedLoads,
        'csr-accepted': this.acceptedLoads,
        'csr-unverified': this.unverifiedLoads,
        'csr-dispatched': this.dispatchedLoads,
        'csr-started': this.startedLoads
    };

    constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string
        , private authService: AuthService, private dataService: DataService) {
    }

    public awardOffer(offer: Offer) {
        return this.http.post<Offer>(this.baseUrl + 'api/Offer/AwardOffer', offer, httpOptions);
    }

    public deleteCarrierEquipmentById(id: number) {
        return this.http.delete<boolean>(this.baseUrl + 'api/CarrierEquipment/Delete/?id=' + id);
    }

    public deleteCarrierServiceMapsByCarrierId(carrierID: number) {
        return this.http.post<boolean>(this.baseUrl + 'api/Carrier/DeleteCarrierServiceMapsByCarrierID', carrierID, httpOptions);
    }

    public deleteQuoteOrderLineCarrierServiceMapsByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.post<boolean>(this.baseUrl + 'api/Carrier/DeleteQuoteOrderLineCarrierServiceMapsByQuoteOrderLineID', quoteOrderLineID, httpOptions);
    }

    //public getActiveCarriers() {
    //    return this.http.get<Carrier[]>(this.baseUrl + 'api/Carrier/GetActiveCarriers');
    //}

    public getSearchCarrierByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierByCarrierID/?carrierID=' + carrierID);
    }

    public getSearchCarrierRMISCertificationByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierRMISCertificationByCarrierID/?carrierID=' + carrierID);
    }

    public rmisAttachOrDetachCarrier(carrierID: number, attachOrDetach: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/RMISAttachOrDetachCarrier/?carrierID=' + carrierID + "&attachOrDetach=" + attachOrDetach);
    }

    public getAllSearchCarrierLookup(lookupType: string, lookupValue: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetAllSearchCarrierLookup/?lookupType=' + lookupType + "&lookupValue=" + lookupValue);
    }

    public getCarrierTeamTypes() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierTeamTypes/');
    }

    public getSearchCarrierTeamByCarrierTeamTypeID(carrierTeamTypeID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamByCarrierTeamTypeID/?carrierTeamTypeID=' + carrierTeamTypeID);
    }

    public getSearchCarrierTeamByEmployeeID(employeeID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamByEmployeeID/?employeeID=' + employeeID);
    }

    public getSearchCarrierTeamByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamByCarrierID/?carrierID=' + carrierID);
    }

    public getSearchCarrierTeamByCarrierTeamTypeAbbreviation(carrierTeamTypeAbbreviation: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamByCarrierTeamTypeAbbreviation/?carrierTeamTypeAbbreviation=' + carrierTeamTypeAbbreviation);
    }

    public getSearchCarrierTeamByCarrierTeamTypeDescription(carrierTeamTypeDescription: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamByCarrierTeamTypeDescription/?carrierTeamTypeDescription=' + carrierTeamTypeDescription);
    }

    public getSearchCarrierTeamEmployeeByCarrierTeamTypeDescription(carrierTeamTypeDescription: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierTeamEmployeeByCarrierTeamTypeDescription/?carrierTeamTypeDescription=' + carrierTeamTypeDescription);
    }

    public saveCarrierTeamMap(carrierID: number, carrierTeamTypeID: number, employeeID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierTeamMap/?carrierID=' + carrierID + '&carrierTeamTypeID=' + carrierTeamTypeID + '&employeeID=' + employeeID);
    }

    public getCarrierIncidentByID(carrierIncidentID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierIncidentByID/?carrierIncidentID=' + carrierIncidentID);
    }

    public saveCarrierIncident(carrierIncident: CarrierIncident) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierIncident/', carrierIncident, httpOptions);
    }

    public deleteCarrierIncident(id: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/DeleteCarrierIncident/?carrierIncidentID=' + id);
    }

    public getCarrierIncidentTypes() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierIncidentTypes');
    }

    public getSearchCarrierIncidentsByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierIncidentsByCarrierID/?carrierID=' + carrierID);
    }

    public getCarrierScoreCard(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierScoreCard/?carrierID=' + carrierID);
    }

    public getCarrierAnalysis(carrierID: number, daysBack: number) {
        return this.http.get<CarrierAnalysis>(this.baseUrl + 'api/Carrier/GetCarrierAnalysis/?carrierID=' + carrierID + '&daysBack=' + daysBack);
    }

    public getDATCarrierByMCNumber(mcNumber: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetDATCarrierByMCNumber/?mcNumber=' + mcNumber);
    }

    public getDATCarrierByDOTNumber(dotNumber: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetDATCarrierByDOTNumber/?dotNumber=' + dotNumber);
    }

    public getCarriersByLineId(quoteOrderLineId: number) {
        return this.http.get<CarrierList[]>(this.baseUrl + 'api/CarrierSales/GetCarriersByLineId/?id=' + quoteOrderLineId);
    }

    public getCarriersByGeocodeLane(oCityID: number, dCityID: number, modeID: number, trailerTypeID: number, rateIndexID: number, isRoundTrip: boolean) {
        return this.http.get<CarrierList[]>(this.baseUrl + 'api/CarrierSales/GetCarriersByGeocodeLane/?oCityID=' + oCityID + '&dCityID=' + dCityID + '&modeID=' + modeID + '&trailerTypeID=' + trailerTypeID + '&rateIndexID=' + rateIndexID + '&isRoundTrip=' + isRoundTrip);
    }

    public getCarriersByQuoteOrderLineIDGeocodeLaneID(quoteOrderLineID: number, geocodeLaneID: number) {
        return this.http.get<CarrierList[]>(this.baseUrl + 'api/CarrierSales/GetCarriersByQuoteOrderLineIDGeocodeLaneID/?quoteOrderLineID=' + quoteOrderLineID + '&geocodeLaneID=' + geocodeLaneID);
    }

    public checkCarrierRMISQualification(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/CheckCarrierRMISQualification/?carrierID=' + carrierID);
    }

    public getSearchCarrier(criteria: SearchCarrierCriteria) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrier/', criteria, httpOptions);
    }

    public getSearchCarrierList(criteria: SearchCarrierListCriteria) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierList/', criteria, httpOptions);
    }

    public getEquipmentClasses() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/TrailerType/GetEquipmentClasses/');
    }

    public getSearchTrailerEquipmentClass() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/TrailerType/GetAllSearchTrailerEquipmentClass/');
    }

    public getCarrierContractByID(carrierContractID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierContractByID/?carrierContractID=' + carrierContractID);
    }

    public getCarrierContractByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierContractByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public getCarrierContractByOfferID(offerID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierContractByOfferID/?offerID=' + offerID);
    }

    public getSearchCarrierContractIncomplete() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierContractIncomplete/');
    }

    public getSearchCarrierContractLatest() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierContractLatest/');
    }

    public getSearchCarrierContractByQuoteOrderID(quoteOrderID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierContractByQuoteOrderID/?quoteOrderID=' + quoteOrderID);
    }

    public saveCarrierContract(carrierContract: CarrierContract) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierContract/', carrierContract, httpOptions);
    }

    public getCarrierLaneByID(carrierLaneID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierLaneByID/?carrierLaneID=' + carrierLaneID);
    }

    public getCarrierLaneByCarrierIDGeocodeLaneID(carrierID: number, geocodeLaneID: number, quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetCarrierLaneByCarrierIDGeocodeLaneID/?carrierID=' + carrierID + '&geocodeLaneID=' + geocodeLaneID + '&quoteOrderLineID=' + quoteOrderLineID);
    }

    public getSearchCarrierLaneByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierLaneByCarrierID/?carrierID=' + carrierID);
    }

    public getSearchCarrierLanesByZip3(oZip3: string, dZip3: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetSearchCarrierLanesByZip3/?oZip3=' + oZip3 + '&dZip3=' + dZip3);
    }

    public saveCarrierLane(carrierLane: CarrierLane) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierLane/', carrierLane, httpOptions);
    }

    public saveCarrierLaneFromSearchCarrierLane(laneList: SearchCarrierLane[]) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierLaneFromSearchCarrierLane/', laneList, httpOptions);
    }

    public getCarrierEquipmentByID(carrierEquipmentID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierEquipment/GetCarrierEquipmentByID/?carrierEquipmentID=' + carrierEquipmentID);
    }

    public getEquipmentByCarrierId(carrierId: number) {
        return this.http.get<CarrierEquipment[]>(this.baseUrl + 'api/CarrierEquipment/GetCarrierEquipmentByCarrierId/?carrierId=' + carrierId);
    }

    public getSearchCarrierEquipmentByID(carrierEquipmentID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierEquipment/GetSearchCarrierEquipmentByID/?carrierEquipmentID=' + carrierEquipmentID);
    }

    public getSearchCarrierEquipmentByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierEquipment/GetSearchCarrierEquipmentByCarrierID/?carrierID=' + carrierID);
    }

    public getCarrierRepsAssignedById(carrierRepsAssignedId: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/GetCarrierRepsAssignedById/?id=' + carrierRepsAssignedId);
    }

    public getCarrierRepsAssignedByEmployeeIdLineId(employeeId: number, quoteOrderLineId: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/GetCarrierRepsAssignedByEmployeeIdLineId/?employeeId=' + employeeId + '&quoteOrderLineId=' + quoteOrderLineId);
    }

    public getCarrierRepsAssignedAvailableByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/GetCarrierRepsAssignedAvailableByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public getCarrierRepsAssignedStatsByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/GetCarrierRepsAssignedStatsByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public deleteCarrierRepsAssigned(carrierRepsAssignedID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/DeleteCarrierRepsAssignedByID/?carrierRepsAssignedID=' + carrierRepsAssignedID);
    }

    public saveCarrierRepsAssigned(carrierRep: CarrierRepsAssigned) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/SaveCarrierRepsAssigned', carrierRep, httpOptions);
    }

    public saveAllCarrierRepsAssigned(quoteOrderLineID: number, carrierReps: CarrierRepsAssigned[]) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierRepsAssigned/SaveAllCarrierRepsAssigned', { quoteOrderLineID: quoteOrderLineID, carrierRepsAssignedList: carrierReps }, httpOptions);
    }

    //public getOpenLoadsFromRoutePath(routePath: string, email): Observable<OpenLoad[]> {
    //    switch (routePath) {
    //        case 'available':
    //            return this.getAvailableLoads(email);
    //        case 'quote':
    //            return this.getAvailableQuoteLoads(email);
    //        case 'rfq':
    //            return this.getAvailableRFQLoads(email);
    //        case 'open-dedicated':
    //            return this.getAvailableOpenDedicatedLoads(email);
    //        case 'booked':
    //            return this.getBookedLoads(email);
    //        case 'tendered':
    //            return this.getTenderedLoads(email);
    //        case 'accepted':
    //            return this.getAcceptedLoads(email);
    //        case 'unverified':
    //            return this.getUnverifiedLoads(email);
    //        case 'dispatched':
    //            return this.getDispatchedLoads(email);
    //        case 'started':
    //            return this.getStartedLoads(email);
    //        default:
    //            return this.getAvailableLoads(email);
    //    }
    //}

    public getSearchCarrierSalesAvailableLoads() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesAvailableLoads/');
    }

    public getSearchCarrierSalesAvailableLoadsByOpportunityID(opportunityID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesAvailableLoadsByOpportunityID/?opportunityID=' + opportunityID);
    }

    public getSearchCarrierSalesBookedLoads() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesBookedLoads/');
    }

    public getSearchCarrierSalesBookedLoadsNegativeProfit() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesBookedLoadsNegativeProfit/');
    }

    public getSearchCarrierSalesLoads() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesLoads/');
    }

    public getSearchCarrierSalesReleasedLoads() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesReleasedLoads/');
    }

    public getSearchCarrierSalesUnverifiedLoads() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesUnverifiedLoads/');
    }

    public getSearchCarrierSalesUnverifiedLoadsInTransit() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesUnverifiedLoadsInTransit/');
    }

    public getSearchCarrierSalesAvailableLoadsByDateRange(startDate: Date, endDate: Date) {
        var params = new HttpParams()
            .set('startDate', startDate.toDateString())
            .set('endDate', endDate.toDateString());
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesAvailableLoadsByDateRange/', { params });
    }

    public getSearchCarrierSalesBookedLoadsByDateRange(startDate: Date, endDate: Date) {
        var params = new HttpParams()
            .set('startDate', startDate.toDateString())
            .set('endDate', endDate.toDateString());
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesBookedLoadsByDateRange/', { params });
    }

    public getSearchCarrierSalesLoadsByDateRange(startDate: Date, endDate: Date) {
        var params = new HttpParams()
            .set('startDate', startDate.toDateString())
            .set('endDate', endDate.toDateString());
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesLoadsByDateRange/', { params });
    }

    public getSearchCarrierSalesReleasedLoadsByDateRange(startDate: Date, endDate: Date) {
        var params = new HttpParams()
            .set('startDate', startDate.toDateString())
            .set('endDate', endDate.toDateString());
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesReleasedLoadsByDateRange/', { params });
    }

    public getSearchCarrierSalesUnverifiedLoadsByDateRange(startDate: Date, endDate: Date) {
        var params = new HttpParams()
            .set('startDate', startDate.toDateString())
            .set('endDate', endDate.toDateString());
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesUnverifiedLoadsByDateRange/', { params });
    }

    public getSearchCarrierSalesLoadsByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesAvailableLoadsByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public getSearchCarrierSalesAvailableLoadsByOrderNumber(orderNumber: string) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetSearchCarrierSalesAvailableLoadsByOrderNumber/?orderNumber=' + orderNumber);
    }

    public getSearchOfferByID(offerID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetSearchOfferByID/?offerID=' + offerID);
    }

    public getSearchOfferByQuoteOrderID(quoteOrderID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetSearchOfferByQuoteOrderID/?quoteOrderID=' + quoteOrderID);
    }

    public getSearchOfferByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetSearchOfferByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public getSearchOfferOpenOffers() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetSearchOfferOpenOffers/');
    }

    public getCustomerCarrierExclusionMapByID(exclusionMapID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetCustomerCarrierExclusionMapByID/?exclusionMapID=' + exclusionMapID);
    }

    public getCustomerCarrierExclusionMapByCustomerIDCarrierID(customerID: number, carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetCustomerCarrierExclusionMapByCustomerIDCarrierID/?customerID=' + customerID + '&carrierID=' + carrierID);
    }

    public getCustomerCarrierExclusionMapByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetCustomerCarrierExclusionMapByCarrierID/?carrierID=' + carrierID);
    }

    public getCustomerCarrierExclusionMapByCustomerID(customerID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetCustomerCarrierExclusionMapByCustomerID/?customerID=' + customerID);
    }

    public getSearchCustomerCarrierExclusionMapByCarrierID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetSearchCustomerCarrierExclusionMapByCarrierID/?carrierID=' + carrierID);
    }

    public getSearchCustomerCarrierExclusionMapByCustomerID(customerID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/GetSearchCustomerCarrierExclusionMapByCustomerID/?customerID=' + customerID);
    }

    public saveCustomerCarrierExclusionMap(exclusionMap: CustomerCarrierExclusionMap) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Customer/SaveCustomerCarrierExclusionMap', exclusionMap, httpOptions);
    }

    public deleteCustomerCarrierExclusionMap(exclusionMapID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Customer/DeleteCustomerCarrierExclusionMap/?exclusionMapID=' + exclusionMapID);
    }

    //public getAvailableLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetAvailableLoads/?email=' + email);
    //}

    //public getAcceptedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetAcceptedLoads/?email=' + email);
    //}

    //public getDispatchedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetDispatchedLoads/?email=' + email);
    //}

    //public getStartedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetStartedLoads/?email=' + email);
    //}

    //public getAvailableQuoteLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetAvailableQuoteLoads/?email=' + email);
    //}

    //public getAvailableRFQLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetAvailableRFQLoads/?email=' + email);
    //}

    //public getAvailableOpenDedicatedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetAvailableOpenDedicatedLoads/?email=' + email);
    //}

    //public getBookedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetBookedLoads/?email=' + email);
    //}

    //public getUnverifiedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetUnverifiedLoads/?email=' + email);
    //}

    //public getInTransitLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetInTransitLoads/?email=' + email);
    //}

    public getEIASeries() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetEIASeries');
    }

    public getFuelRates() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetFuelRates');
    }

    public getFuelRateByID(fuelRateID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetFuelRateByID/?fuelRateID=' + fuelRateID);
    }

    public getFuelRateTierByID(fuelRateTierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetFuelRateTierByID/?fuelRateTierID=' + fuelRateTierID);
    }

    public getFuelRateTiersByFuelRateID(fuelRateID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetFuelRateTiersByFuelRateID/?fuelRateID=' + fuelRateID);
    }

    public saveFuelRate(fuelRate: FuelRate) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierSales/SaveFuelRate', fuelRate, httpOptions);
    }

    public saveFuelRateTier(fuelRateTier: FuelRateTier) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierSales/SaveFuelRateTier', fuelRateTier, httpOptions);
    }

    //public getGeocodeDetailsByLineId(quoteOrderLineId: number) {
    //    return this.http.get<GeocodeDetails>(this.baseUrl + 'api/CarrierSales/GetGeocodeDetailsByLineId/?id=' + quoteOrderLineId);
    //}

    //public getOpenLoads() {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetOpenLoads/?email=' + this.authService.user.username);
    //}

    public getOrderStopsByLineId(quoteOrderLineId: number) {
        //return this.http.get<OrderStop[]>(this.baseUrl + 'api/OrderStop/GetStopsByLineId/?id=' + quoteOrderLineId);
        return this.http.get<APIResponse>(this.baseUrl + 'api/OrderStop/GetStopsByLineId/?id=' + quoteOrderLineId);
    }

    public getQuoteOrderLineById(quoteOrderLineId: number) {
        return this.http.get<QuoteOrderLine>(this.baseUrl + 'api/QuoteOrderLine/Get/?id=' + quoteOrderLineId);
    }

    public getOfferByID(offerID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetOfferByID/?offerID=' + offerID);
    }

    public getSearchOfferByOfferBatchID(offerBatchID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetSearchOfferByOfferBatchID/?offerBatchID=' + offerBatchID);
    }

    public getOffersByOfferBatchID(offerBatchID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetOffersByOfferBatchId/?offerBatchID=' + offerBatchID);
    }

    public deleteOfferBatch(offerBatchID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/deleteOfferBatch/?offerBatchID=' + offerBatchID);
    }

    public getOffersByLineIdObs(quoteOrderLineId: number) {
        //return this.http.get<Offer[]>(this.baseUrl + 'api/Offer/GetOffersByLineId/?id=' + quoteOrderLineId);
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetOffersByLineId/?id=' + quoteOrderLineId);
    }

    public getOffersByLineId(quoteOrderLineId: number) {
        //return this.http.get<Offer[]>(this.baseUrl + 'api/Offer/GetOffersByLineId/?id=' + quoteOrderLineId);
        this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetOffersByLineId/?id=' + quoteOrderLineId)
            .subscribe(result => {
                var datCol: DataCollection = {
                    name: "getOffersByLineId-" + quoteOrderLineId,
                    resultCollection: result.resultCollection
                }
                this.dataService.set(datCol);
                //console.log("cseService.getOffersByLineId", result.resultCollection);
                //this._offers.next(result.resultCollection);
                //this.setOffers(result.resultCollection);
        });
    }

    //public setOffers(offers: Offer[]) {
    //    this._offers.next(offers);
    //}

    //public getOffers(): Observable<Array<Offer>> {
    //    return this._offers.asObservable();
    //}

    //public clearOffers() {
    //    this._offers.next();
    //}

    public getOpportunityCostTypes() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Opportunity/GetOpportunityCostTypes');
    }

    public getQuoteOrderLineOpportunityCostByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Opportunity/GetQuoteOrderLineOpportunityCostByQuoteOrderLineID?quoteOrderLineID=' + quoteOrderLineID);
    }

    public saveQuoteOrderLineOpportunityCost(cost: QuoteOrderLineOpportunityCost) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Opportunity/SaveQuoteOrderLineOpportunityCost', cost, httpOptions);
    }

    public getAllCarrierServices() {
        return this.http.get<CarrierService[]>(this.baseUrl + 'api/Carrier/GetAllCarrierServices');
    }

    public getCarrierServicesRequiredForLoads() {
        return this.http.get<CarrierService[]>(this.baseUrl + 'api/Carrier/GetCarrierServicesRequiredForLoads');
    }

    public getCarrierServiceByID(carrierServiceID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierServiceByID?carrierServiceID=' + carrierServiceID);
    }

    public saveCarrierService(carrierService: CarrierService) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveCarrierService', carrierService, httpOptions);
    }

    public getServiceMapsByCarrierId(carrierID: number) {
        return this.http.get<CarrierServiceMap[]>(this.baseUrl + 'api/Carrier/GetServiceMapsByCarrierID/?carrierID=' + carrierID);
    }

    public getQuoteOrderLineServiceMapsByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<QuoteOrderLineCarrierServiceMap[]>(this.baseUrl + 'api/Carrier/GetQuoteOrderLineServiceMapsByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    public getSearchQuoteOrderLineCarrierServiceByQuoteOrderLineID(quoteOrderLineID: number) {
        return this.http.get<SearchQuoteOrderLineCarrierService[]>(this.baseUrl + 'api/Carrier/GetSearchQuoteOrderLineCarrierServiceByQuoteOrderLineID/?quoteOrderLineID=' + quoteOrderLineID);
    }

    //public getTenderedLoads(email: string) {
    //    return this.http.get<OpenLoad[]>(this.baseUrl + 'api/CarrierSales/GetTenderedLoads/?email=' + email);
    //}

    public getTenderMaintenanceByID(tenderMaintenanceID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/CarrierSales/GetTenderMaintenanceByID/?tenderMaintenanceID=' + tenderMaintenanceID);
    }

    public saveTenderMaintenance(tenderMaintenance: TenderMaintenance) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/CarrierSales/SaveTenderMaintenance', tenderMaintenance, httpOptions);
    }

    public getZip3sByLineId(quoteOrderLineId: number) {
        return this.http.get<Zip3[]>(this.baseUrl + 'api/City/GetZip3sByLineId/?id=' + quoteOrderLineId);
    }

    public saveCarrier(carrier: Carrier, userEnteredZip: string) {
        return this.http.post<Carrier>(this.baseUrl + 'api/Carrier/Save/?userEnteredZip=' + userEnteredZip, carrier, httpOptions);
    }

    public saveAwardCarrierFromCarrierSelection(quoteOrderLineList: QuoteOrderLine[]) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Carrier/SaveAwardCarrierFromCarrierSelection', quoteOrderLineList, httpOptions);
    }

    public saveCarrierEquipment(carrierEquipment: CarrierEquipment) {
        return this.http.post<CarrierEquipment>(this.baseUrl + 'api/CarrierEquipment/Save', carrierEquipment, httpOptions);
    }

    public saveCarrierServiceMaps(carrierServiceMaps: CarrierServiceMap[]) {
        return this.http.post<CarrierServiceMap[]>(this.baseUrl + 'api/Carrier/SaveCarrierServiceMapList', carrierServiceMaps, httpOptions);
    }

    public saveQuoteOrderLineCarrierServiceMapList(serviceMapList: QuoteOrderLineCarrierServiceMap[]) {
        return this.http.post<QuoteOrderLineCarrierServiceMap[]>(this.baseUrl + 'api/Carrier/SaveQuoteOrderLineCarrierServiceMapList', serviceMapList, httpOptions);
    }

    public saveQuoteOrderLineCarrierServiceMap(serviceMap: QuoteOrderLineCarrierServiceMap) {
        return this.http.post<QuoteOrderLineCarrierServiceMap>(this.baseUrl + 'api/Carrier/SaveQuoteOrderLineCarrierServiceMap', serviceMap, httpOptions);
    }

    public saveOfferComment(offer: Offer) {
        return this.http.post<Offer>(this.baseUrl + 'api/Offer/SaveOfferComment/', offer, httpOptions);
    }

    public saveOfferFromCarrierList(carrierList: CarrierList) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Offer/SaveOfferFromCarrierDetails/', carrierList, httpOptions);
    }

    public saveOfferFromSearchCarrierSalesBookedLoads(load: SearchCarrierSalesBookedLoads) {
        return this.http.post<SearchCarrierSalesBookedLoads>(this.baseUrl + 'api/Offer/SaveOfferFromSearchCarrierSalesBookedLoads/', load, httpOptions);
    }

    public saveOfferFromSearchCarrierSalesUnverifiedLoads(load: SearchCarrierSalesUnverifiedLoads) {
        return this.http.post<SearchCarrierSalesUnverifiedLoads>(this.baseUrl + 'api/Offer/SaveOfferFromSearchCarrierSalesUnverifiedLoads/', load, httpOptions);
    }

    public saveOfferFromBulkTenderCarrierList(carrierList: CarrierList[]) {
        return this.http.post<CarrierList[]>(this.baseUrl + 'api/Offer/SaveOfferFromBulkTenderCarrierDetails/', carrierList, httpOptions);
    }

    public saveAwardOfferFromCarrierSelection(offerList: Offer[]) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Offer/SaveAwardOfferFromCarrierSelection/', offerList, httpOptions);
    }

    public getAllCarrierDropReasons() {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetAllCarrierDropReasons/');
    }

    public getCarrierDropReasonByID(carrierDropReasonID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Offer/GetCarrierDropReasonByID/?carrierDropReasonID=' + carrierDropReasonID);
    }

    public saveCarrierDropReason(carrierDropReason: CarrierDropReason) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Offer/SaveCarrierDropReason', carrierDropReason, httpOptions);
    }

    public removeAwardOffer(offer: Offer) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Offer/RemoveCarrierFromOffer/', offer, httpOptions);
    }

    public deleteOffer(offer: Offer) {
        return this.http.post<APIResponse>(this.baseUrl + 'api/Offer/DeleteOffer/', offer, httpOptions);
    }

    public getCarrierByID(carrierID: number) {
        return this.http.get<APIResponse>(this.baseUrl + 'api/Carrier/GetCarrierByID/?carrierID=' + carrierID);
    }

    public searchCarrier(searchText: string) {
        return this.http.get<Carrier>(this.baseUrl + 'api/Carrier/SearchCarrier/?search=' + searchText);
    }

    //public verifyLoadByOpenLoad(openLoad: OpenLoad) {
    //    return this.http.post<OpenLoad>(this.baseUrl + 'api/CarrierSales/VerifyLoad', openLoad, httpOptions);
    //}
}

//@Injectable()
//export class OpenLoadsService extends BehaviorSubject<GridDataResult> {
//    private loading: boolean = true;
//    private openLoads: Observable<GridDataResult> | undefined;

//    constructor(private http: HttpClient, @Inject('BASE_URL') private baseUrl: string) {
//        super({ data: [], total: 0 });
//    }

//    public query(state: State): void {
//        if (!this.openLoads) this.openLoads = this.getOpenLoads();
//        this.openLoads.subscribe(x => super.next(x));
//        //this.openLoads.take(5).subscribe(x => super.next(x));
//    }

//    private getOpenLoads(): Observable<GridDataResult> {
//        this.loading = true;

//        if (this.openLoads) {
//            return this.openLoads
//                .pipe(
//                    map(response => (<GridDataResult>{
//                        data: response.data,
//                        total: response.total
//                    })),
//                    tap(() => this.loading = false)
//                );
//        }

//        return this.http
//            .get(this.baseUrl + 'api/CarrierSales/GetOpenLoads')
//            .pipe(
//                map(response => (<GridDataResult>{
//                    data: response,
//                    total: (response as OpenLoad[]).length
//                })),
//                tap(() => this.loading = false)
//            );
//    }
//}
